export const STANDARD_LABOR_PRODUCT_NAMES = [
  'LABOR:INSTALLATION',
  'LABOR:ROUGH-IN',
  'LABOR:ENGINEERING',
  'LABOR:PROGRAMMING',
  'LABOR:PROJECT MANAGEMENT',
] as const

export type LaborProductSearchSortable = {
  name?: string | null
  productnumber?: string | null
}

export type ProductSearchIdentifiable = {
  Id: string
}

export function mergePinnedProductSearchResults<T extends ProductSearchIdentifiable>(
  sortedResults: T[] | undefined,
  pinnedResults: T[] | undefined,
  laborOnly: boolean
): T[] | undefined {
  if (!laborOnly) return sortedResults

  const pinned = pinnedResults ?? []
  const seen = new Set(pinned.map((r) => r.Id))
  const remaining = (sortedResults ?? []).filter((r) => !seen.has(r.Id))
  return [...pinned, ...remaining]
}

export function shouldShowStandardLaborLoading({
  laborOnly,
  hasQuery,
  hasDisplayResults,
  isFetchingStandardLabor,
}: {
  laborOnly: boolean
  hasQuery: boolean
  hasDisplayResults: boolean
  isFetchingStandardLabor: boolean
}): boolean {
  return laborOnly && !hasQuery && !hasDisplayResults && isFetchingStandardLabor
}

export function encodeODataLiteral(value: string): string {
  return encodeURIComponent(value.replace(/'/g, "''"))
}

// Backend refuses server-side product creation for flat-price SI labor and
// stamps this issue on the unmapped entry — the only working resolution is
// mapping to an existing Other Charge product. Substring is a cross-repo
// contract with integrations-api's _FLAT_PRICE_LABOR_PRODUCT_ISSUE.
export function requiresOtherChargeProduct(item: { mapped_product_issue?: string | null }) {
  return !!item.mapped_product_issue?.includes('Other Charge product')
}

export function buildProductContainsFilter(search: string): string {
  // Dataverse `contains()` is case-insensitive; `tolower()` is not implemented
  // by the Web API and returns 501, so never wrap fields with it.
  return search
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((term) => {
      const encoded = encodeODataLiteral(term)
      const fields = ['name', 'productnumber', 'dsy_partnumber', 'description']
      return `(${fields
        .map((field) => `(${field} ne null and contains(${field}, '${encoded}'))`)
        .join(' or ')})`
    })
    .join(' and ')
}

export function buildStandardLaborProductFilter(): string {
  return STANDARD_LABOR_PRODUCT_NAMES.map((name) => {
    const encoded = encodeODataLiteral(name)
    return `(name eq '${encoded}' or productnumber eq '${encoded}')`
  }).join(' or ')
}

export function sortStandardLaborProducts<T extends LaborProductSearchSortable>(rows: T[]): T[] {
  const order = new Map<string, number>(STANDARD_LABOR_PRODUCT_NAMES.map((name, index) => [name, index]))
  return [...rows].sort((a, b) => {
    const aKey = (a.name ?? a.productnumber ?? '').toUpperCase()
    const bKey = (b.name ?? b.productnumber ?? '').toUpperCase()
    return (order.get(aKey) ?? Number.MAX_SAFE_INTEGER) - (order.get(bKey) ?? Number.MAX_SAFE_INTEGER)
  })
}
